import { create } from 'zustand';

export type SidebarState = 'expanded' | 'collapsed' | 'hidden';

const SIDEBAR_STATE_KEY = 'sidebar-state';
const CONTRAST_KEY = 'ui-contrast';
const BRIGHTNESS_KEY = 'ui-brightness';

const sidebarCycle: Record<SidebarState, SidebarState> = {
  expanded: 'collapsed',
  collapsed: 'hidden',
  hidden: 'expanded',
};

function getInitialSidebarState(): SidebarState {
  const stored = localStorage.getItem(SIDEBAR_STATE_KEY);
  if (stored === 'expanded' || stored === 'collapsed' || stored === 'hidden') return stored;
  return 'expanded';
}

function getInitialDarkMode(): boolean {
  const stored = localStorage.getItem('theme');
  if (stored === 'dark') return true;
  if (stored === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyDarkClass(dark: boolean) {
  document.documentElement.classList.toggle('dark', dark);
}

function getInitialContrast(): number {
  const stored = localStorage.getItem(CONTRAST_KEY);
  if (stored) {
    const val = parseFloat(stored);
    if (!isNaN(val) && val >= 0.5 && val <= 1.5) return val;
  }
  return 1;
}

function getInitialBrightness(): number {
  const stored = localStorage.getItem(BRIGHTNESS_KEY);
  if (stored) {
    const val = parseFloat(stored);
    if (!isNaN(val) && val >= 0.5 && val <= 1.5) return val;
  }
  return 1;
}

function applyVisualAdjustments(contrast: number, brightness: number) {
  document.documentElement.style.setProperty('--ui-contrast', String(contrast));
  document.documentElement.style.setProperty('--ui-brightness', String(brightness));
}

interface UIState {
  sidebarState: SidebarState;
  darkMode: boolean;
  contrast: number;
  brightness: number;
  toggleSidebar: () => void;
  setSidebarState: (state: SidebarState) => void;
  toggleDarkMode: () => void;
  setDarkMode: (dark: boolean) => void;
  setContrast: (contrast: number) => void;
  setBrightness: (brightness: number) => void;
  resetVisualAdjustments: () => void;
}

const initialDark = getInitialDarkMode();
const initialContrast = getInitialContrast();
const initialBrightness = getInitialBrightness();

applyDarkClass(initialDark);
applyVisualAdjustments(initialContrast, initialBrightness);

export const useUIStore = create<UIState>()((set) => ({
  sidebarState: getInitialSidebarState(),
  darkMode: initialDark,
  contrast: initialContrast,
  brightness: initialBrightness,
  toggleSidebar: () =>
    set((state) => {
      const next = sidebarCycle[state.sidebarState];
      localStorage.setItem(SIDEBAR_STATE_KEY, next);
      return { sidebarState: next };
    }),
  setSidebarState: (sidebarState) => {
    localStorage.setItem(SIDEBAR_STATE_KEY, sidebarState);
    set({ sidebarState });
  },
  toggleDarkMode: () =>
    set((state) => {
      const next = !state.darkMode;
      localStorage.setItem('theme', next ? 'dark' : 'light');
      applyDarkClass(next);
      return { darkMode: next };
    }),
  setDarkMode: (dark) => {
    localStorage.setItem('theme', dark ? 'dark' : 'light');
    applyDarkClass(dark);
    set({ darkMode: dark });
  },
  setContrast: (contrast) => {
    localStorage.setItem(CONTRAST_KEY, String(contrast));
    applyVisualAdjustments(contrast, useUIStore.getState().brightness);
    set({ contrast });
  },
  setBrightness: (brightness) => {
    localStorage.setItem(BRIGHTNESS_KEY, String(brightness));
    applyVisualAdjustments(useUIStore.getState().contrast, brightness);
    set({ brightness });
  },
  resetVisualAdjustments: () => {
    localStorage.setItem(CONTRAST_KEY, '1');
    localStorage.setItem(BRIGHTNESS_KEY, '1');
    applyVisualAdjustments(1, 1);
    set({ contrast: 1, brightness: 1 });
  },
}));
